import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import { Loader2, Save, Sliders, RotateCcw, DollarSign } from 'lucide-react';

interface MultiSignalSettings {
  storeId: number;
  signalWeights: {
    spend: number;
    impressions: number;
    clicks: number;
    conversions: number;
  };
}

type WeightKey = 'spend' | 'impressions' | 'clicks' | 'conversions';

const DEFAULT_WEIGHTS: Record<WeightKey, number> = {
  spend: 25,
  impressions: 15,
  clicks: 30,
  conversions: 30,
};

const SIGNAL_INFO: readonly { key: WeightKey; label: string; color: string; bgColor: string }[] = [
  { key: 'spend', label: 'Spend', color: '#F59E0B', bgColor: 'bg-amber-50 dark:bg-amber-950/40' },
  { key: 'impressions', label: 'Impressions', color: '#3B82F6', bgColor: 'bg-blue-50 dark:bg-blue-950/40' },
  { key: 'clicks', label: 'Clicks', color: '#10B981', bgColor: 'bg-emerald-50' },
  { key: 'conversions', label: 'Conversions', color: '#8B5CF6', bgColor: 'bg-purple-50 dark:bg-purple-950/40' },
];

const HYPOTHETICAL_REVENUE = 1000;

export function MultiSignalSection({ storeId }: { readonly storeId: number }) {
  const queryClient = useQueryClient();
  const [weights, setWeights] = useState<Record<WeightKey, number>>(DEFAULT_WEIGHTS);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { isLoading } = useQuery({
    queryKey: ['admin', 'multi-signal-settings', storeId],
    queryFn: async ({ signal }) => {
      const res = await api.get<MultiSignalSettings>(
        `/api/v1/analytics/halo-settings?storeId=${storeId}`,
        { signal },
      );
      if (res.success && res.data?.signalWeights) {
        const w = res.data.signalWeights;
        if (typeof w.spend === 'number') setWeights(w);
      }
      return res.data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (newWeights: Record<WeightKey, number>) => {
      const res = await api.patch<MultiSignalSettings>(
        `/api/v1/analytics/halo-settings?storeId=${storeId}`,
        { signalWeights: newWeights },
      );
      if (!res.success) throw new Error(res.error ?? 'Failed to save');
      return res.data;
    },
    onSuccess: () => {
      setMessage({ type: 'success', text: 'Signal weights saved' });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'multi-signal-settings', storeId] });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (err: Error) => {
      setMessage({ type: 'error', text: err.message });
      setTimeout(() => setMessage(null), 5000);
    },
  });

  function handleSliderChange(key: WeightKey, value: number) {
    setWeights((prev) => {
      const updated = { ...prev, [key]: value };
      const total = Object.values(updated).reduce((s, v) => s + v, 0);
      if (total === 0) return updated;
      const normalized: Record<string, number> = {};
      for (const [k, v] of Object.entries(updated)) {
        normalized[k] = Math.round((v / total) * 100);
      }
      const normTotal = Object.values(normalized).reduce((s, v) => s + v, 0);
      const diff = 100 - normTotal;
      if (diff !== 0) {
        normalized[key] = (normalized[key] ?? 0) + diff;
      }
      return normalized as Record<WeightKey, number>;
    });
  }

  function handleReset() {
    setWeights(DEFAULT_WEIGHTS);
  }

  const total = Object.values(weights).reduce((s, v) => s + v, 0);

  const impactPreview = useMemo(() => {
    return SIGNAL_INFO.map((sig) => ({
      ...sig,
      value: Math.round((weights[sig.key] / 100) * HYPOTHETICAL_REVENUE),
    }));
  }, [weights]);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-violet-50 flex items-center justify-center">
            <Sliders className="w-3.5 h-3.5 text-violet-600" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Multi-Signal Weights</h3>
        </div>
        <button
          onClick={handleReset}
          className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <RotateCcw className="w-3 h-3" />
          Reset
        </button>
      </div>

      {isLoading ? (
        <div className="animate-pulse space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-10 bg-gray-100 dark:bg-gray-800 rounded-lg" />
          ))}
        </div>
      ) : (
        <div>
          {/* Sliders */}
          <div className="space-y-4 mb-4">
            {SIGNAL_INFO.map((sig) => (
              <div key={sig.key}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: sig.color }} />
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-200">{sig.label}</span>
                  </div>
                  <span className="text-xs font-bold text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">
                    {weights[sig.key]}%
                  </span>
                </div>
                <div className="relative">
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={weights[sig.key]}
                    onChange={(e) => handleSliderChange(sig.key, parseInt(e.target.value, 10))}
                    className="w-full h-2 rounded-full appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, ${sig.color} ${weights[sig.key]}%, #e5e7eb ${weights[sig.key]}%)`,
                    }}
                  />
                  {/* Tick marks */}
                  <div className="flex justify-between px-0.5 mt-1">
                    {[0, 25, 50, 75, 100].map((tick) => (
                      <span key={tick} className="text-[8px] text-gray-300">{tick}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Stacked bar */}
          <div className="flex h-3 rounded-full overflow-hidden mb-4">
            {SIGNAL_INFO.map((sig) => (
              <div
                key={sig.key}
                className="transition-all duration-300"
                style={{ width: `${weights[sig.key]}%`, backgroundColor: sig.color }}
                title={`${sig.label}: ${weights[sig.key]}%`}
              />
            ))}
          </div>

          {/* Signal Impact Preview */}
          <div className="bg-gray-50 dark:bg-gray-950 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-1.5 mb-2">
              <DollarSign className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
              <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Signal Impact on ${HYPOTHETICAL_REVENUE.toLocaleString()} Revenue
              </span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {impactPreview.map((sig) => (
                <div key={sig.key} className={`text-center p-2 rounded-lg ${sig.bgColor}`}>
                  <span className="text-sm font-bold" style={{ color: sig.color }}>
                    ${sig.value}
                  </span>
                  <span className="text-[10px] text-gray-500 dark:text-gray-400 block">{sig.label}</span>
                </div>
              ))}
            </div>
          </div>

          {total !== 100 && (
            <p className="text-[10px] text-amber-600 mb-3">Weights sum to {total}% (will be normalized to 100%)</p>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={() => saveMutation.mutate(weights)}
              disabled={saveMutation.isPending}
              className="flex-1 px-4 py-2.5 text-sm font-medium bg-gray-900 dark:bg-gray-950 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {saveMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save Weights
            </button>
          </div>

          {message && (
            <div className={`flex items-center gap-2 mt-3 px-3 py-2 rounded-lg text-xs font-medium border ${
              message.type === 'success'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-red-50 text-red-700 dark:text-red-400 border-red-200'
            }`}>
              {message.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
